import React, { FC, useMemo } from 'react'
import { useForm } from 'react-hook-form'
import * as yup from 'yup'
import { yupResolver } from '@hookform/resolvers/yup'

import BootstrapModalLayout from '../../../ui/BootstrapModalLayout'
import useText from '../../../common/hooks/useText'
import { Alert } from '../../../lib/alert'
import { Company, Partner } from '../../../common/redux/api/web-api'
import Input from '../../../common/components/Input'
import Select from '../../../common/components/Select'
import { useToast } from '../../../common/uikit/ToastProvider'

export type CreateOrganisationModalProps = {
    isOpen: boolean
    onSubmit: (company: Company) => Promise<unknown>
    onClose: () => void
    partners: Partner[]
}

export const CreateOrganisationModal: FC<CreateOrganisationModalProps> = ({ isOpen, onClose, partners, onSubmit }) => {
    const text = useText()
    const toast = useToast()

    const schema = yup.object().shape({
        name: yup.string().required(text('msgNameRequired')),
        description: yup.string().required(text('msgDescriptionRequired')),
        email: yup.string().email(text('msgEmailInvalid')).optional(),
        phone: yup.string().optional(),
        partnerId: yup.string().required(),
    })

    const {
        handleSubmit,
        reset,
        register,
        formState: { isValid, isSubmitting, errors },
        setError,
    } = useForm<Company>({
        resolver: yupResolver(schema, { abortEarly: false }),
        mode: 'onBlur',
        reValidateMode: 'onChange',
    })

    const onCancel = () => {
        onClose()
        reset()
    }

    const onSave = handleSubmit(async (data) => {
        try {
            await onSubmit(data)
            onCancel()
            toast.notifySuccess(text('msgResourceCreated', { resource: text('lblOrganisation') }))
        } catch (err) {
            setError('root', { message: text('msgCreateCompanyError') })
        }
    })

    const partnerOptions = useMemo(
        () => partners.map((partner) => ({ key: partner.id, value: partner.name }) as { key: number; value: string }),
        [partners]
    )

    return isOpen ? (
        <BootstrapModalLayout
            title={text('lblNewOrganisation')}
            actions={[
                {
                    label: text('btnSave'),
                    callback: onSave,
                    disabled: !isValid || isSubmitting,
                    loading: isSubmitting,
                },
                {
                    label: text('btnCancel'),
                    callback: onCancel,
                },
            ]}
        >
            <form name="form" role="form" noValidate>
                {errors.root?.message ? <Alert error message={errors.root?.message} /> : null}

                <div className="form-group">
                    <Input
                        labelText={text('lblName')}
                        {...register('name')}
                        id="input-name"
                        error={errors.name?.message}
                    />
                </div>

                <div className="form-group">
                    <Input
                        labelText={text('lblDescription')}
                        {...register('description')}
                        id="input-description"
                        error={errors.description?.message}
                    />
                </div>

                <div className="form-group">
                    <Input
                        labelText={text('lblEmail')}
                        {...register('email')}
                        id="input-email"
                        error={errors.email?.message}
                    />
                </div>

                <div className="form-group">
                    <Input labelText={text('lblPhone')} {...register('phone')} id="input-phone" />
                </div>

                <div className="form-group">
                    <Select
                        id="input-partner"
                        enableDefaultOption
                        labelText={text('lblPartnerField')}
                        {...register('partnerId')}
                        options={partnerOptions}
                    />
                </div>
            </form>
        </BootstrapModalLayout>
    ) : null
}
